import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@/store/store";
import { PaymentState, PaymentTransaction, PaymentSummary, PaymentFilter, PaymentSortBy, PaymentPagination } from "@/types/payment";

const initialState: PaymentState = {
  filters: [],
  fetchFilters: [],
  sortBy: [],
  pagination: { 
    pageSize: 25, 
    pageNumber: 1, 
    page: 1, 
    per_page: 25, 
    total: 0, 
    total_pages: 0 
  },
  searchQuery: "",
  paymentsData: null,
  paymentSummary: null,
  selectedTab: "incoming",
  selectedTransaction: null,
  transactionDetails: null,
  isLoading: false,
  isDetailsLoading: false,
  error: null,
};

const PaymentSlice = createSlice({
  name: "paymentHistory",
  initialState,
  reducers: {
    addFilter: (state) => {
      const defaultFilter: PaymentFilter = {
        field: { label: "", value: "" },
        operator: "",
        value: [],
      };
      const hasEmptyValue = state.filters.some(
        (filter: any) => (!filter.value || filter.value.length === 0) && filter.operator !== "is_empty"
      );
      if (!hasEmptyValue) {
        state.filters.unshift(defaultFilter);
      }
    },
    updateFilter: (state, action: PayloadAction<{ index: number; key: keyof PaymentFilter; value: any }>) => {
      const { index, key, value } = action.payload;
      if (state.filters[index]) {
        state.filters[index][key] = value;
      }
    },
    removeFilter: (state, action: PayloadAction<number>) => {
      state.filters.splice(action.payload, 1);
      if (state.filters.length === 0) {
        state.fetchFilters = [];
      }
    },
    resetFilters: (state) => {
      state.filters = [];
      state.fetchFilters = [];
    },
    setFetchFilter: (state, action: PayloadAction<any[] | undefined>) => {
      if (action.payload) {
        // If filters are provided, use them directly
        state.fetchFilters = action.payload;
      } else {
        // Otherwise, use the current filters from state
        state.fetchFilters = state.filters.map(({ field, operator, value }) => ({
          field,
          operator: (operator as any)?.value ?? operator,
          value,
        }));
      }
    },
    setFilter: (state, action: PayloadAction<any>) => {
      state.filters = action.payload;
    },
    setPaymentData: (state, action: PayloadAction<PaymentTransaction[] | null>) => {
      state.paymentsData = action.payload;
    },
    setPaymentSummary: (state, action: PayloadAction<PaymentSummary | null>) => {
      state.paymentSummary = action.payload;
    },
    setSelectedTransaction: (state, action: PayloadAction<PaymentTransaction | null>) => {
      state.selectedTransaction = action.payload;
    },
    setTransactionDetails: (state, action: PayloadAction<PaymentTransaction | null>) => {
      state.transactionDetails = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setSortBy: (state, action: PayloadAction<PaymentSortBy[]>) => {
      state.sortBy = action.payload;
    },
    setPagination: (state, action: PayloadAction<PaymentPagination>) => {
      state.pagination = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setDetailsLoading: (state, action: PayloadAction<boolean>) => {
      state.isDetailsLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  addFilter,
  updateFilter,
  removeFilter,
  resetFilters,
  setPaymentData,
  setPaymentSummary,
  setSelectedTransaction,
  setTransactionDetails,
  setSearchQuery,
  setSortBy,
  setPagination,
  setFetchFilter,
  setFilter,
  setLoading,
  setDetailsLoading,
  setError,
} = PaymentSlice.actions;

export const paymentSelector = (state: RootState) => state.paymentHistory;

export default PaymentSlice.reducer;

